import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import {
	useAutomationTemplateIdQuery,
	useInfiniteBoardsQuery,
	useStartAutomationForContactMutation,
} from '../../../../../queries';
import { useArchiveOpportunity } from '../../../../../queries/Boards/useArchiveOpportunity';
import { useDeleteOpportunity } from '../../../../../queries/Boards/useDeleteOpportunity';
import {
	invalidateGetOpportunitiesByCompany,
	useGetOpportunitiesByCompany,
} from '../../../../../queries/Boards/useGetOpportunitiesByCompany';
import {
	invalidateGetOpportunitiesByContact,
	useGetOpportunitiesByContact,
} from '../../../../../queries/Boards/useGetOpportunitiesByContact';
import {
	getDefaultDateStringValue,
	getEntityDisplayName,
	numberToCurrencyStringValue,
} from '../../../../../models/UiUtils';
import { Button } from '../../../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../../components-v2/Dropdown';
import { Pill } from '../../../../components-v2/Pill';
import { THEME } from '../../../../components-v2/Theme/theme';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { Modal } from '../../../Modal';
import { DataboardsIcon } from '../../../svgs/icons/DataboardsIcon';
import { EditOpportunityModal } from '../EditOpportunity';
import { DataBoardRow, DataBoardTable } from '../../DataBoardTable';
import { OpportunityNameLink } from '../OpportunityNameLink';
import { styleSheet } from './styles';

const _EntityOpportunities = ({
	className,
	entity,
	styles,
	isContactMode,
}: {
	className?: string;
	entity: Api.IContact | Api.ICompany;
	styles?: StyleDeclarationValue[];
	isContactMode: boolean;
}) => {
	const errorMessages = useErrorMessages();
	const [opportunityToAdd, setOpportunityToAdd] = React.useState<Api.IOpportunity>(null);

	// Automation-related state
	const [pendingAutomationTemplateId, setPendingAutomationTemplateId] = React.useState<string>(null);
	const [triggeringOpportunity, setTriggeringOpportunity] = React.useState<Api.IOpportunity>(null);

	const userSession = useUserSession();
	const boardsQuery = useInfiniteBoardsQuery({
		type: 'Opportunity',
		expand: 'Stage.Tag',
		autoFetchAll: true,
		meta: {
			toastErrorMessage: 'Error loading boards',
			errorLogging: { action: 'EntityOpportunitiesInfiniteBoardsQuery-Error', category: 'Opportunities' },
		},
	});

	const boards = React.useMemo(() => {
		return boardsQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [boardsQuery.data]);

	// Derive stages that have automations directly from boards data.
	// Replaces the previous useEffect + TagViewModel.load() loop.
	const stagesWithAutomations = React.useMemo<Api.IAccountTag[]>(() => {
		return boards.flatMap(board =>
			(board.stages ?? [])
				.filter(
					(stage): stage is Api.IBoardStageReference & { tag: Api.IAccountTag } => !!stage.tag?.automationTemplateId
				)
				.map(stage => stage.tag)
		);
	}, [boards]);

	const opportunitiesByContactQuery = useGetOpportunitiesByContact({
		enabled: isContactMode,
		contactId: entity.id,
	});
	const opportunitiesByCompanyQuery = useGetOpportunitiesByCompany({
		enabled: !isContactMode,
		companyId: entity.id,
	});

	const activeOpportunitiesQuery = isContactMode ? opportunitiesByContactQuery : opportunitiesByCompanyQuery;

	const getAutomationTemplateByIdQuery = useAutomationTemplateIdQuery({
		enabled: !!pendingAutomationTemplateId,
		id: pendingAutomationTemplateId,
	});

	const automationTemplate = getAutomationTemplateByIdQuery.data;

	const startAutomationForContactMutation = useStartAutomationForContactMutation({
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			setTriggeringOpportunity(null);
			setPendingAutomationTemplateId(null);
		},
	});

	const invalidateOpportunities = () => {
		if (isContactMode) {
			invalidateGetOpportunitiesByContact({ contactId: entity.id });
		} else {
			invalidateGetOpportunitiesByCompany({ companyId: entity.id });
		}
	};

	const archiveMutation = useArchiveOpportunity({
		onSuccess: invalidateOpportunities,
	});

	const deleteMutation = useDeleteOpportunity({
		onSuccess: invalidateOpportunities,
	});

	const ignoreAutomation = () => {
		setTriggeringOpportunity(null);
		setPendingAutomationTemplateId(null);
	};

	const startAutomation = () => {
		if (!automationTemplate || !triggeringOpportunity) return;

		startAutomationForContactMutation.mutate({
			template: automationTemplate,
			contactId: triggeringOpportunity.primaryContact.id,
			start: true,
		});
	};

	const onAddItemClicked = () => {
		if (isContactMode) {
			const primaryContact = entity as Api.IContact;
			setOpportunityToAdd({
				primaryContact,
				name: primaryContact.firstName + ' ' + primaryContact.lastName,
			});
		} else {
			setOpportunityToAdd({
				company: entity,
				name: entity.companyName,
			});
		}
	};

	const onOpportunitySave = (opportunity: Api.IOpportunity) => {
		const wasCreating = !opportunityToAdd?.id;
		const originalStageId = opportunityToAdd?.boardStage?.id;
		const newStageId = opportunity.boardStage?.id;
		const stageChanged = originalStageId !== newStageId;

		setOpportunityToAdd(null);

		// Check for automations when creating new opportunities OR when editing and stage changed
		if ((wasCreating || stageChanged) && newStageId) {
			// Find the board that contains this stage
			const board = boards.find(b => (b.stages ?? []).some(s => s.id === newStageId));
			if (board) {
				const stageName = (board.stages ?? []).find(x => x.id === newStageId)?.name;
				const toStageName = `${board.name}: ${stageName}`;
				const pendingAutomationForStage = stagesWithAutomations.find(
					x => x.name === toStageName && !!x.automationTemplateId
				);

				if (pendingAutomationForStage?.automationTemplateId) {
					setTriggeringOpportunity(opportunity);
					setPendingAutomationTemplateId(pendingAutomationForStage.automationTemplateId);
				}
			}
		}

		invalidateGetOpportunitiesByContact({ contactId: entity.id });
		if (opportunity.company?.id) {
			invalidateGetOpportunitiesByCompany({ companyId: opportunity.company.id });
		}
	};

	const opportunities = React.useMemo<Api.IOpportunity[]>(() => {
		return (activeOpportunitiesQuery.data?.values ?? []) as Api.IOpportunity[];
	}, [activeOpportunitiesQuery.data]);

	const openOpportunities = opportunities.filter(opportunity => !opportunity.isArchived);
	const pipeline = openOpportunities.reduce((total, opportunity) => total + (opportunity.dealSize ?? 0), 0);
	const summary = opportunities.length
		? `${openOpportunities.length} open · $${numberToCurrencyStringValue(pipeline)} pipeline`
		: undefined;

	const rows: DataBoardRow[] = opportunities.map(opportunity => {
		const tags = opportunity.tagsCollection?.length
			? opportunity.tagsCollection.map(tag => ({ key: tag.id ?? tag.name, name: tag.name, color: tag.color }))
			: (opportunity.tags ?? []).map(tagName => ({
					key: tagName,
					name: tagName,
					color: undefined as Api.IAccountTag['color'],
				}));
		return {
			id: opportunity.id,
			name: <OpportunityNameLink opportunity={opportunity} />,
			statusTitle: opportunity.boardStage?.name,
			status: opportunity.boardStage?.name ? (
				<Pill variant='label' color='blue' label={opportunity.boardStage.name} />
			) : (
				'-'
			),
			fields: [
				opportunity.primaryContact ? getEntityDisplayName(opportunity.primaryContact) : '-',
				opportunity.closeDate ? getDefaultDateStringValue(opportunity.closeDate) : '-',
				opportunity.assignees?.[0] ? getEntityDisplayName(opportunity.assignees[0]) : '-',
			],
			value: `$${numberToCurrencyStringValue(opportunity.dealSize ?? 0)}`,
			details: tags.length
				? [
						{
							label: 'Tags',
							value: (
								<span className={css(styleSheet.tags)}>
									{tags.map(tag => (
										<Pill key={tag.key} variant='tag' label={tag.name} color={tag.color} />
									))}
								</span>
							),
						},
					]
				: undefined,
			actions: (
				<Dropdown>
					<DropdownEllipsisTrigger aria-label={`Actions for ${opportunity.name || 'opportunity'}`} />
					<DropdownContent>
						{!opportunity.isArchived ? (
							<DropdownItem onClick={() => archiveMutation.mutate({ itemId: opportunity.id })}>Archive</DropdownItem>
						) : null}
						<DropdownItem onClick={() => setOpportunityToAdd(opportunity)}>
							{opportunity.isArchived ? 'Restore' : 'Edit'}
						</DropdownItem>
						<DropdownItem onClick={() => deleteMutation.mutate({ itemId: opportunity.id })}>
							Permanently Delete
						</DropdownItem>
					</DropdownContent>
				</Dropdown>
			),
		};
	});

	return (
		<div className={`${css(...(styles || []))} ${className || ''}`}>
			<DataBoardTable
				icon={<DataboardsIcon height={16} width={21} fill={THEME.colors.primary.contrastText} />}
				iconColor='green'
				title='Current Opportunities'
				summary={summary}
				addItemName='Opportunity'
				onAdd={onAddItemClicked}
				nameLabel='Opportunity'
				statusLabel='Stage'
				fieldLabels={['Contact', 'Close date', 'Owner']}
				valueLabel='Value'
				rows={rows}
				isLoading={activeOpportunitiesQuery.isLoading}
				emptyText={
					activeOpportunitiesQuery.isError
						? 'Couldn’t load opportunities.'
						: !activeOpportunitiesQuery.isFetching
							? `No opportunities exist for this ${isContactMode ? 'contact' : 'company'}.`
							: undefined
				}
			/>

			{opportunityToAdd ? (
				<EditOpportunityModal
					opportunity={opportunityToAdd}
					onSave={onOpportunitySave}
					onCancel={() => setOpportunityToAdd(null)}
					boards={boards}
				/>
			) : null}

			{userSession?.account?.features?.automation?.enabled && (
				<Modal isOpen={!!pendingAutomationTemplateId} onRequestClose={ignoreAutomation} useDefaultHeader={true}>
					<div className={css(styleSheet.automationPrompt)}>
						<div className={css(styleSheet.automationLine)}>This tag can trigger an automation flow:</div>
						{getAutomationTemplateByIdQuery.isSuccess ? (
							<>
								<div className={css(styleSheet.automationLine, styleSheet.automationName)}>
									{automationTemplate?.name || ''}
								</div>
								<Button variant='primary' size='sm' styles={[styleSheet.automationButton]} onClick={startAutomation}>
									Start Automation
								</Button>
							</>
						) : (
							<LoadingSpinner type='tiny' />
						)}
					</div>
				</Modal>
			)}
		</div>
	);
};

export const EntityOpportunities = observer(_EntityOpportunities);
